import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  FaUserEdit,
  FaIdCard,
  FaAddressCard,
  FaCar,
  FaVideo,
  FaCheckCircle,
} from "react-icons/fa";
import API from "../../config/config";
import axiosInstance from "../../config/axiosInstance";

import StepProfilePhoto from "./StepProfilePhoto";
import StepDrivingLicense from "./StepDrivingLicense";
import StepAadhaar from "./StepAadhaar";
import StepPAN from "./StepPAN";
import StepChoices from "./StepChoices";
import StepVideos from "./StepVideos";
import StepBankDetails from "./StepBankDetails";
import StepSuccess from "./StepSuccess";

export default function StepperLayout() {
  const { id } = useParams();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);

  const stepRef = useRef(null); // ref for current step

  const fetchDriverDetails = async (shouldResetStep = true) => {
    try {
      const token = localStorage.getItem("adminToken");
      const { data } = await axiosInstance.get(
        `${API.ADMIN.DRIVER_DETAIL}/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const profile = data.data;
      setDriver(profile);
      setFormFromProfile(profile, shouldResetStep);
    } catch (error) {
      console.error("Error fetching driver details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverDetails();
  }, [id]);

  const setFormFromProfile = (profile, shouldResetStep = true) => {
    const toInputDateFormat = (dateStr) => {
      if (!dateStr) return "";
      if (dateStr.includes("-")) {
        const parts = dateStr.split("-");
        if (parts[2]?.length === 4) {
          return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(
            2,
            "0"
          )}`;
        }
      }
      return new Date(dateStr).toISOString().split("T")[0];
    };

    const drivingChoices = profile?.drivingChoices || {};
    const bankDetails = profile?.bankAccountDetails || {};
    const dlDetails = profile?.drivingLincenseDetails || {};
    const aadhaarDetails = profile?.aadhaarDetails || {};
    const panDetails = profile?.panDetails || {};
    const trainingVideos = profile?.trainingVideos || {};

    const initialForm = {
      profilePhoto: profile.profileImage || null,

      dlNumber: dlDetails.dlNumber || "",
      nameToMatch: dlDetails.nameToMatch || "",
      dateOfBirth: toInputDateFormat(dlDetails.dateOfBirth),
      dateOfIssue: toInputDateFormat(dlDetails.dateOfIssue),
      dateOfExpire: toInputDateFormat(dlDetails.dateOfExpire),
      dlfront: dlDetails.dlfront || null,
      dlback: dlDetails.dlback || null,

      aadhaarNumber: aadhaarDetails.aadhaarNumber || "",
      aadhaarFront: aadhaarDetails.aadhaarfront || null,
      aadhaarBack: aadhaarDetails.aadhaarback || null,
      aadhaarNameToMatch: aadhaarDetails.fullName || "",

      panNumber: panDetails.panNumber || "",
      holderName: panDetails.holderName || "",
      panFile: panDetails.panfront || null,

      tripType: drivingChoices.tripType || "",
      carTransmission: drivingChoices.carTransmission || "",
      carTypes: Array.isArray(drivingChoices.carTypes)
        ? drivingChoices.carTypes
        : drivingChoices.carTypes
        ? [drivingChoices.carTypes]
        : [],
      city: drivingChoices.city || "",
      area: drivingChoices.area || "",

      beneficiaryName: bankDetails.beneficiaryName || "",
      accountNumber: bankDetails.accountNumber || "",
      ifsc: bankDetails.ifsc || "",
      bankName: bankDetails.bankName || "",

      carType: trainingVideos.carType || "",
      transmission_video: trainingVideos.transmission_video || "",
      front_drive_video: trainingVideos.front_drive_video || "",
      reverse_parking_video: trainingVideos.reverse_parking_video || "",
    };

    setFormData(initialForm);

    // Only reset step on initial load, not on refresh
    if (shouldResetStep) {
      const firstIncomplete = steps.findIndex(
        (step) => !step.isComplete(initialForm, profile)
      );
      console.log(
        "Setting current step on initial load. First incomplete:",
        firstIncomplete
      );
      setCurrentStep(firstIncomplete === -1 ? 0 : firstIncomplete);
    } else {
      console.log(
        "Skipping step reset on refresh. Current step remains:",
        currentStep
      );
    }
  };

  const steps = [
    {
      key: "profilePhoto",
      label: "Profile Photo",
      icon: <FaUserEdit />,
      component: StepProfilePhoto,
      isComplete: (form, profile) => form.profilePhoto || profile?.profileImage,
    },
    {
      key: "dl",
      label: "DL Details",
      icon: <FaIdCard />,
      component: StepDrivingLicense,
      isComplete: (form, profile) => {
        const dl = profile?.drivingLincenseDetails || {};
        return (
          dl.status === "InProgress" ||
          dl.status === "Verified" ||
          ((form.dlNumber || dl.dlNumber) &&
            (form.nameToMatch || dl.nameToMatch) &&
            (form.dateOfBirth || dl.dateOfBirth) &&
            (form.dateOfIssue || dl.dateOfIssue) &&
            (form.dateOfExpire || dl.dateOfExpire) &&
            (form.dlfront || dl.dlfront) &&
            (form.dlback || dl.dlback))
        );
      },
    },
    {
      key: "aadhaar",
      label: "Aadhaar Details",
      icon: <FaAddressCard />,
      component: StepAadhaar,
      isComplete: (form, profile) => {
        const a = profile?.aadhaarDetails || {};
        return (
          a.status === "InProgress" ||
          a.status === "Verified" ||
          ((form.aadhaarNumber || a.aadhaarNumber) &&
            (form.aadhaarFront || a.aadhaarfront) &&
            (form.aadhaarBack || a.aadhaarback) &&
            (form.aadhaarNameToMatch || a.fullName))
        );
      },
    },
    {
      key: "pan",
      label: "PAN Details",
      icon: <FaIdCard />,
      component: StepPAN,
      isComplete: (form, profile) => {
        const pan = profile?.panDetails || {};
        return (
          pan.status === "InProgress" ||
          pan.status === "Verified" ||
          ((form.panNumber || pan.panNumber) &&
            (form.panFile || pan.panfront) &&
            (form.holderName || pan.holderName))
        );
      },
    },
    // {
    //   key: "bank",
    //   label: "Bank Details",
    //   icon: <FaAddressCard />,
    //   component: StepBankDetails,
    //   isComplete: (form, profile) => {
    //     const bank = profile?.bankAccountDetails || {};
    //     return (
    //       (form.beneficiaryName || bank.beneficiaryName) &&
    //       (form.accountNumber || bank.accountNumber) &&
    //       (form.ifsc || bank.ifsc) &&
    //       (form.bankName || bank.bankName)
    //     );
    //   },
    // },
    {
      key: "choices",
      label: "Driving Choices",
      icon: <FaCar />,
      component: StepChoices,
      isComplete: (form, profile) => {
        const choice = profile?.drivingChoices || {};

        // Check if all required fields are present in the saved profile data
        const hasRequiredData =
          choice.tripType &&
          choice.carTransmission &&
          choice.city &&
          choice.area &&
          (choice.carTypes || choice.carType); // Accept either field name

        console.log("Driving choices completion check:", {
          hasRequiredData,
          tripType: choice.tripType,
          carTransmission: choice.carTransmission,
          city: choice.city,
          area: choice.area,
          carTypes: choice.carTypes,
          carType: choice.carType,
        });

        return hasRequiredData;
      },
    },
    // {
    //   key: "videos",
    //   label: "Driving Videos",
    //   icon: <FaVideo />,
    //   component: StepVideos,
    //   isComplete: (form, profile) => {
    //     const v = profile?.trainingVideos || {};
    //     return (
    //       (form.transmission_video || v.transmission_video) &&
    //       (form.front_drive_video || v.front_drive_video) &&
    //       (form.reverse_parking_video || v.reverse_parking_video) &&
    //       (form.carType || v.carType)
    //     );
    //   },
    // },
    {
      key: "success",
      label: "Complete",
      icon: null,
      component: StepSuccess,
      isComplete: (form, profile) => {
        // Check if all previous steps are complete (manually check each step)
        const profilePhoto = form.profilePhoto || profile?.profileImage;
        const dlComplete = (() => {
          const dl = profile?.drivingLincenseDetails || {};
          return (
            dl.status === "InProgress" ||
            dl.status === "Verified" ||
            ((form.dlNumber || dl.dlNumber) &&
              (form.nameToMatch || dl.nameToMatch) &&
              (form.dateOfBirth || dl.dateOfBirth) &&
              (form.dateOfIssue || dl.dateOfIssue) &&
              (form.dateOfExpire || dl.dateOfExpire) &&
              (form.dlfront || dl.dlfront) &&
              (form.dlback || dl.dlback))
          );
        })();
        const aadhaarComplete = (() => {
          const a = profile?.aadhaarDetails || {};
          return (
            a.status === "InProgress" ||
            a.status === "Verified" ||
            ((form.aadhaarNumber || a.aadhaarNumber) &&
              (form.aadhaarFront || a.aadhaarfront) &&
              (form.aadhaarBack || a.aadhaarback) &&
              (form.aadhaarNameToMatch || a.fullName))
          );
        })();
        const panComplete = (() => {
          const pan = profile?.panDetails || {};
          return (
            pan.status === "InProgress" ||
            pan.status === "Verified" ||
            ((form.panNumber || pan.panNumber) &&
              (form.panFile || pan.panfront) &&
              (form.holderName || pan.holderName))
          );
        })();
        const drivingChoicesComplete = (() => {
          const choice = profile?.drivingChoices || {};
          return (
            choice.tripType &&
            choice.carTransmission &&
            choice.city &&
            choice.area &&
            (choice.carTypes || choice.carType)
          );
        })();

        return (
          profilePhoto &&
          dlComplete &&
          aadhaarComplete &&
          panComplete &&
          drivingChoicesComplete
        );
      },
    },
  ];

  // Define a function to check if all required steps are complete
  const areAllStepsComplete = (form, profile) => {
    return steps.slice(0, -1).every((step) => step.isComplete(form, profile));
  };

  // For driving choices step, check if form is valid rather than if it's saved
  const isDrivingChoicesFormValid = () => {
    if (steps[currentStep].key !== "choices") return false;
    return (
      formData.tripType &&
      formData.carTransmission &&
      formData.carTypes &&
      formData.carTypes.length > 0 &&
      formData.city &&
      formData.area
    );
  };

  // Validation functions for each step
  const isProfilePhotoFormValid = () => {
    if (steps[currentStep].key !== "profilePhoto") return false;
    return formData.profilePhoto;
  };

  const isDLFormValid = () => {
    if (steps[currentStep].key !== "dl") return false;
    return (
      formData.dlNumber &&
      formData.nameToMatch &&
      formData.dateOfBirth &&
      formData.dateOfIssue &&
      formData.dateOfExpire &&
      formData.dlfront &&
      formData.dlback
    );
  };

  const isAadhaarFormValid = () => {
    if (steps[currentStep].key !== "aadhaar") return false;
    return (
      formData.aadhaarNumber &&
      formData.aadhaarNameToMatch &&
      formData.aadhaarFront &&
      formData.aadhaarBack
    );
  };

  const isPANFormValid = () => {
    if (steps[currentStep].key !== "pan") return false;
    return formData.panNumber && formData.holderName && formData.panFile;
  };

  const isBankFormValid = () => {
    if (steps[currentStep].key !== "bank") return false;
    return (
      formData.beneficiaryName &&
      formData.accountNumber &&
      formData.ifsc &&
      formData.bankName
    );
  };

  const isVideosFormValid = () => {
    if (steps[currentStep].key !== "videos") return false;
    return (
      formData.carType &&
      formData.transmission_video &&
      formData.front_drive_video &&
      formData.reverse_parking_video
    );
  };

  // Main form validation function
  const isCurrentStepFormValid = () => {
    switch (steps[currentStep].key) {
      case "profilePhoto":
        return isProfilePhotoFormValid();
      case "dl":
        return isDLFormValid();
      case "aadhaar":
        return isAadhaarFormValid();
      case "pan":
        return isPANFormValid();
      case "choices":
        return isDrivingChoicesFormValid();
      case "bank":
        return isBankFormValid();
      case "videos":
        return isVideosFormValid();
      default:
        return false;
    }
  };

  const isCurrentStepComplete =
    isCurrentStepFormValid() || steps[currentStep].isComplete(formData, driver);

  const allStepsComplete = areAllStepsComplete(formData, driver);

  // Debug logging
  console.log("Stepper state:", {
    currentStep,
    currentStepKey: steps[currentStep].key,
    totalSteps: steps.length,
    stepKeys: steps.map((s) => s.key),
    isCurrentStepComplete,
    isDrivingChoicesFormValid: isDrivingChoicesFormValid(),
    allStepsComplete,
    formData:
      steps[currentStep].key === "choices"
        ? {
            tripType: formData.tripType,
            carTransmission: formData.carTransmission,
            carTypes: formData.carTypes,
            city: formData.city,
          }
        : "not choices step",
    driverDrivingChoices: driver?.drivingChoices,
  });

  const handleNext = async () => {
    console.log("handleNext called, currentStep:", currentStep);
    console.log("isCurrentStepComplete:", isCurrentStepComplete);
    console.log("allStepsComplete:", allStepsComplete);

    if (stepRef.current?.validateAndUpload) {
      try {
        setLoading(true);
        const result = await stepRef.current.validateAndUpload();
        if (result === "unchanged") {
          console.log("No changes made in this step.");
        }
        console.log("validateAndUpload result:", result);
      } catch (error) {
        console.error("Upload error:", error);
        alert("❌ Failed to upload: " + error.message);
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    } else if (stepRef.current?.validateAndSubmit) {
      try {
        setLoading(true);
        const result = await stepRef.current.validateAndSubmit();
        if (result === "unchanged") {
          console.log("No changes made in this step.");
        }
        console.log("validateAndSubmit result:", result);
      } catch (error) {
        console.error("Submit error:", error);
        alert("❌ Failed to submit: " + error.message);
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    // Proceed to next step
    console.log(
      "About to proceed to next step. Current step:",
      currentStep,
      "Total steps:",
      steps.length
    );
    console.log(
      "Check: currentStep < steps.length - 1:",
      currentStep,
      "<",
      steps.length - 1,
      "=",
      currentStep < steps.length - 1
    );

    if (currentStep < steps.length - 1) {
      console.log("Moving to next step:", currentStep + 1);
      setCurrentStep((prevStep) => prevStep + 1);
    } else {
      // ✅ When last step is complete
      console.log("At final step - navigating to driver detail page");
      navigate(`/drivers/detail/${id}`);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleFinish = () => {
    console.log("All steps completed!");
    navigate(`/drivers/detail/${driver._id}`); // or wherever your route is
  };

  const CurrentComponent = steps[currentStep].component;

  const getInitialFormDataForStep = (stepKey) => {
    if (!driver) return {};

    switch (stepKey) {
      case "dl":
        const dl = driver?.drivingLincenseDetails || {};
        return {
          dlNumber: dl.dlNumber || "",
          nameToMatch: dl.nameToMatch || "",
          dateOfBirth: dl.dateOfBirth || "",
          dateOfIssue: dl.dateOfIssue || "",
          dateOfExpire: dl.dateOfExpire || "",
          dlfront: dl.dlfront || null,
          dlback: dl.dlback || null,
        };

      case "aadhaar":
        const a = driver?.aadhaarDetails || {};
        return {
          aadhaarNumber: a.aadhaarNumber || "",
          aadhaarFront: a.aadhaarfront || null,
          aadhaarBack: a.aadhaarback || null,
          aadhaarNameToMatch: a.fullName || "",
        };

      case "pan":
        const p = driver?.panDetails || {};
        return {
          panNumber: p.panNumber || "",
          panFile: p.panfront || null,
          holderName: p.holderName || "",
        };

      case "bank":
        const b = driver?.bankAccountDetails || {};
        return {
          beneficiaryName: b.beneficiaryName || "",
          accountNumber: b.accountNumber || "",
          ifsc: b.ifsc || "",
          bankName: b.bankName || "",
        };

      case "choices":
        const d = driver?.drivingChoices || {};
        return {
          tripType: d.tripType || "",
          carTransmission: d.carTransmission || "",
          carTypes: Array.isArray(d.carTypes) ? d.carTypes : [],
          city: d.city || "",
          area: d.area || "",
        };

      case "videos":
        const v = driver?.trainingVideos || {};
        return {
          carType: v.carType || "",
          transmission_video: v.transmission_video || "",
          front_drive_video: v.front_drive_video || "",
          reverse_parking_video: v.reverse_parking_video || "",
        };

      case "profilePhoto":
        return {
          profilePhoto: driver?.profileImage || null,
        };

      default:
        return {};
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-50 to-white px-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl border border-blue-100 p-4 md:p-6">
        {/* Stepper */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-10 space-y-4 md:space-y-0 md:space-x-4">
          {steps.map((step, index) => (
            <div
              key={step.key}
              className="flex flex-col items-center relative flex-1"
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center border-2 font-bold text-lg z-10
                  ${
                    index === currentStep
                      ? "bg-yellow-500 text-white border-yellow-500"
                      : step.isComplete(formData, driver)
                      ? "bg-green-500 text-white border-green-500"
                      : "bg-gray-200 text-gray-600 border-gray-300"
                  }`}
              >
                {index === currentStep ? (
                  index + 1
                ) : step.isComplete(formData, driver) ? (
                  <FaCheckCircle />
                ) : (
                  index + 1
                )}
              </div>
              <div className="text-center text-xs mt-2">{step.label}</div>
              {index !== steps.length - 1 && (
                <div className="hidden md:block absolute top-6 right-[-50%] w-full h-0.5 bg-gray-300 z-0" />
              )}
            </div>
          ))}
        </div>
        {currentStep < steps.length - 1 && (
          // Step Heading
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-blue-700 mb-2">
              Step {currentStep + 1}: {steps[currentStep].label}
            </h2>
          </div>
        )}

        {/* Step Component */}
        <div className="mb-10">
          <CurrentComponent
            ref={stepRef}
            driver={driver}
            formData={formData}
            setFormData={setFormData}
            nextStep={handleNext}
            prevStep={handleBack}
            refreshDriver={() => fetchDriverDetails(false)}
            disabled={allStepsComplete}
            initialFormData={getInitialFormDataForStep(steps[currentStep].key)}
          />
          {loading && (
            <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50 pointer-events-none">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-end mt-6">
          {/* Step 0: Only show Next */}
          {currentStep === 0 && (
            <button
              onClick={handleNext}
              disabled={!isCurrentStepComplete}
              className={`px-6 py-2 rounded-lg transition ${
                isCurrentStepComplete
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Next →
            </button>
          )}

          {/* Intermediate Steps: Show Back and Next */}
          {currentStep > 0 && currentStep < steps.length - 1 && (
            <>
              <button
                onClick={handleBack}
                className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400 transition mr-4"
              >
                ← Back
              </button>
              <button
                onClick={handleNext}
                disabled={!isCurrentStepComplete}
                className={`px-6 py-2 rounded-lg transition ${
                  isCurrentStepComplete
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Next →
              </button>
            </>
          )}

          {/* Final Step: Only show Finish aligned right */}
          {currentStep === steps.length - 1 && (
            <button
              onClick={handleFinish}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
            >
              Finish →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
